import React, { useMemo, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
// @ts-ignore CSS is loaded by the application bundler.
import "../AgGridTheme.css";

import SortHeader from "../SortingHeader";
import SearchBar from "../../SearchBarTable/SearchBarTable";

import {
  ClientSideRowModelModule,
  ModuleRegistry,
  TextFilterModule,
  PaginationModule,
} from "ag-grid-community";

import { FaCubes, FaAlignLeft, FaGlobe } from "react-icons/fa";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
]);

interface MetricTableProps {
  metrics: any[];
  selectedMetric: any | null;
  setSelectedMetric: (metric: any) => void;
  onRowSelect: (metric: any) => void;
  selectedRow: string | null;
}

const MetricTable: React.FC<MetricTableProps> = ({
  metrics,
  onRowSelect,
  selectedRow,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [gridApi, setGridApi] = useState<any>(null);
  const gridRef = useRef<AgGridReact>(null);

  // NEW: Composite key builder (must match MetricSetup)
  const makeKey = (m: any) =>
    `${m.metric_name}|${m.iso_code}|${m.category}|${m.market_type}|${m.fuel_type}|${m.product}`;

  // Handle search
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  // Filter rows
  const filteredMetrics = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return metrics.filter(
      (m) =>
        m.metric_name?.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q) ||
        m.fuel_type?.toLowerCase().includes(q) ||
        m.iso_code?.toLowerCase().includes(q) ||
        m.product?.toLowerCase().includes(q)
    );
  }, [metrics, searchQuery]);

  // Column definition
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Metric",
        field: "metric_name",
        flex: 1,
        minWidth: 250,
        sortable: true,
        sort: 'asc' as const,   // ← FIXED DEFAULT SORT
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">{params.value}</div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Metric",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
      },           
      {
        headerName: "Description",
        field: "description",
        flex: 2,
        minWidth: 350,
        sortable: true,
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">{params.value}</div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Description",
          enableSorting: true,
          icon: <FaAlignLeft className="text-gray-600" />,
        },
      },
      {
        headerName: "Fuel Type",
        field: "fuel_type",
        flex: 1,
        minWidth: 200,
        sortable: true,
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">{params.value}</div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Fuel Type",
          enableSorting: true,
          icon: <FaAlignLeft className="text-gray-600" />,
        },
      },
      {
        headerName: "ISO Code",
        field: "iso_code",
        flex: 1,
        minWidth: 200,
        sortable: true,
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">{params.value}</div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "ISO Code",
          enableSorting: true,
          icon: <FaGlobe className="text-gray-600" />,
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    []
  );

  return (
    <div className="h-full p-4 bg-white shadow-md rounded-xl font-poppins flex flex-col">
      {/* Search Bar */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            placeholder="Search metrics..."
          />
        </div>
      </div>

      {/* AG Grid */}
      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          overflow: "auto",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          theme="legacy"
          ref={gridRef}
          rowData={filteredMetrics}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowHeight={60}
          headerHeight={55}

          /** Pagination + page size selector */
          pagination={true}
          paginationPageSize={35}
          paginationPageSizeSelector={[10, 20, 35, 50, 100]}

          onGridReady={(params) => setGridApi(params.api)}
          onRowClicked={(event) => onRowSelect(event.data)}

          /** Highlight selected row using composite key */
          getRowClass={(params) => {
            const key = makeKey(params.data);
            return key === selectedRow ? "ag-row-selected-custom" : "";
          }}

          getRowId={(params) => makeKey(params.data)}
        />

      </div>
    </div>
  );
};

export default MetricTable;
